import { parsePhoneNumber, mobileLabelString } from '@fs/zion-phone-number'
import { formatBirthDate, parseBirthDate } from '../utils/birthDateUtils'
import {
  churchRecordNumberFieldString,
  emailFieldString,
  firstNamesFieldString,
  lastNamesFieldString,
  usernameFieldString,
} from '../commonStrings'
import { fieldNames } from '../constants'
import { formatFullName } from '../formats'

const keys = {
  [fieldNames.birthDate]: 'cis.profile.birth_date',
  [fieldNames.location]: 'cis.profile.country',
  [fieldNames.email]: 'cis.profile.email',
  [fieldNames.emailVerified]: 'cis.profile.email.verify',
  [fieldNames.gender]: 'cis.profile.gender',
  [fieldNames.firstNames]: 'cis.profile.given_name',
  [fieldNames.lastNames]: 'cis.profile.surname',
  [fieldNames.churchRecordNumber]: 'cis.profile.member_number',
  [fieldNames.smsCountry]: 'cis.profile.sms.country',
  [fieldNames.smsNumber]: 'cis.profile.sms.number',
  [fieldNames.smsVerified]: 'cis.profile.sms.verify',
  [fieldNames.userId]: 'cis.profile.userid',
  [fieldNames.username]: 'cis.profile.username',
  [fieldNames.preferredLang]: 'cis.profile.preferred_lang',
  [fieldNames.helperPin]: 'cis.profile.helper_pin',
}

const verifiedOptions = {
  parse: (x) => (x ? 'T' : 'F'),
  format: (x) => x === 'T',
}

export default function getAccountView(profile, t) {
  const alwaysPrivateLabel = t('data-shared-always-private.label', 'Always Private')
  const femaleLabel = t('gender-female.label', 'Female')
  const maleLabel = t('gender-male.label', 'Male')
  // const notSpecifiedLabel = t('gender-not-specified.label', 'Not Specified')

  const fullNameShared = profile[keys.firstNames]?.shared && profile[keys.lastNames]?.shared

  const accountViewObj = createAccountViewObj.bind(this, profile)

  return {
    alwaysPrivateLabel,
    fullName: {
      name: 'fullName',
      value: formatFullName({
        givenNames: profile[keys.firstNames]?.value,
        surnames: profile[keys.lastNames]?.value,
      }),
      shared: fullNameShared,
      label: t('full-name.label', 'Full Name'),
    },
    firstNames: accountViewObj(fieldNames.firstNames, {
      label: firstNamesFieldString(),
    }),
    lastNames: accountViewObj(fieldNames.lastNames, {
      label: lastNamesFieldString(),
    }),
    username: accountViewObj(fieldNames.username, {
      label: usernameFieldString(),
    }),
    gender: accountViewObj(fieldNames.gender, {
      formattedValue: formatGender(profile[keys.gender]?.value, { femaleLabel, maleLabel }),
      label: t('gender.label', 'Sex'),
      female: {
        label: femaleLabel,
        value: 'FEMALE',
      },
      male: {
        label: maleLabel,
        value: 'MALE',
      },
      // unspecified: {
      //   label: notSpecifiedLabel,
      //   value: null,
      // },
    }),
    birthDate: accountViewObj(fieldNames.birthDate, {
      label: t('birth-date.label', 'Birthdate'),
      parse: parseBirthDate,
      format: formatBirthDate,
    }),
    location: accountViewObj(fieldNames.location),
    email: accountViewObj(fieldNames.email, {
      label: emailFieldString(),
      recoveryLabel: t('recovery-email.label', 'Recovery Email'),
    }),
    emailVerified: accountViewObj(fieldNames.emailVerified, verifiedOptions),
    smsCountry: accountViewObj(fieldNames.smsCountry),
    smsNumber: accountViewObj(fieldNames.smsNumber, {
      label: mobileLabelString(),
      recoveryLabel: t('recovery-mobile-number.label', 'Recovery Mobile Number'),
      shared: profile[keys.smsCountry]?.shared, // only uses the smsCountry.shared value to duplicate legacy behavior
      // international format for use outside PhoneNumber component
      formattedValue: formatMobilePhoneNumber(profile),
    }),
    smsVerified: accountViewObj(fieldNames.smsVerified, verifiedOptions),
    churchRecordNumber: accountViewObj(fieldNames.churchRecordNumber, {
      label: churchRecordNumberFieldString(),
    }),
    preferredLang: accountViewObj(fieldNames.preferredLang),
    helperPin: accountViewObj(fieldNames.helperPin),
  }
}

function getAsAccountItem(profileKey, parse) {
  return function asAccountItem(value, shared) {
    // value ?? this.value logic ensure this works well with the new zion-form in which values are undefined for disabled fields
    return { name: profileKey, value: parse(value ?? this.value), shared: shared ?? this.shared, exists: this.exists }
  }
}

function identity(value) {
  return value
}

function createAccountViewObj(profile, name, { label, parse = identity, format = identity, ...rest } = {}) {
  const profileKey = keys[name]
  const value = profile[profileKey]?.value ?? ''
  return {
    name,
    key: profileKey,
    // Track whether this item exists in CIS or not because we need to make sure
    // not to send empty strings for non-exists data otherwise we will see 465 errors, IDENT-8992
    exists: value !== '',
    value: format(value),
    shared: profile[profileKey]?.shared ?? false,
    label,
    asAccountItem: getAsAccountItem(profileKey, parse),
    ...rest,
  }
}

function formatGender(rawValue, { femaleLabel, maleLabel }) {
  if (rawValue === 'FEMALE') {
    return femaleLabel
  }
  if (rawValue === 'MALE') {
    return maleLabel
  }
  return ''
}

function formatMobilePhoneNumber(profile) {
  const rawSmsNumber = profile[keys.smsNumber]?.value
  const smsCountry = profile[keys.smsCountry]?.value
  return parsePhoneNumber(rawSmsNumber, smsCountry).format('NATIONAL')
}
